import styled from "styled-components";

const bp = { sm: "560px", md: "768px", lg: "1100px" };

const c = {
  ink: "#171717",
  inkSoft: "#5f5a57",
  white: "#ffffff",
  cream: "#fffdfa",
  creamSoft: "#f9f4ee",
  gold: "#c9a227",
  goldDark: "#b88f1d",
  goldLine: "rgba(201,162,39,0.18)",
  pink: "#f6d6e4",
  pinkStrong: "#e88ab2",
  pinkDeep: "#d95e97",
  pinkSoft: "rgba(232,138,178,0.08)",
  border: "rgba(23,23,23,0.08)",
  borderStrong: "rgba(23,23,23,0.11)",
  error: "#bf5c5c",
  success: "#4f7d57",
  shadowSoft: "0 8px 22px rgba(0,0,0,0.04)",
  shadowCard: "0 16px 34px rgba(0,0,0,0.05)",
};

export const Page = styled.section`
  margin-top: 100px;
  min-height: 100vh;
  padding: 36px 16px 90px;
  background: transparent;

  @media (max-width: ${bp.md}) {
    padding: 10px 12px 72px;
  }
`;

export const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

export const Title = styled.h1`
  margin: 40px 0 8px;
  font-size: 60px;
  font-family: "Cormorant Garamond", serif;
  font-weight: 500;
  line-height: 0.95;
  letter-spacing: 0.01em;
  color: ${c.ink};

  @media (max-width: ${bp.md}) {
    margin-top: 0;
    font-size: 42px;
  }
`;

export const Subtitle = styled.p`
  max-width: 720px;
  margin: 0 0 18px;
  font-size: 15px;
  line-height: 1.65;
  color: ${c.inkSoft};

  @media (max-width: ${bp.md}) {
    margin-bottom: 14px;
    font-size: 13.5px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(300px, 0.78fr);
  gap: 16px;
  align-items: start;

  @media (max-width: ${bp.lg}) {
    grid-template-columns: 1fr;
  }
`;

export const Main = styled.div`
  display: grid;
  gap: 12px;
`;

export const PickupHero = styled.section`
  position: relative;
  display: grid;
  gap: 8px;
  padding: 16px 18px;
  border-radius: 20px;
  border: 1px solid rgba(232, 138, 178, 0.16);
  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(255, 251, 253, 0.98) 100%
    ),
    linear-gradient(
      135deg,
      rgba(201, 162, 39, 0.04) 0%,
      rgba(232, 138, 178, 0.04) 100%
    );
  box-shadow: ${c.shadowCard};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 20px;
    pointer-events: none;
    box-shadow: inset 0 0 0 1px rgba(201, 162, 39, 0.04);
  }

  @media (max-width: ${bp.md}) {
    padding: 14px;
    border-radius: 16px;
  }
`;

export const PickupHeroTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export const PickupBadge = styled.span`
  min-height: 28px;
  padding: 0 11px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${c.pinkDeep};
  background: ${c.pinkSoft};
  border: 1px solid rgba(232, 138, 178, 0.16);
`;

export const PickupLine = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${c.inkSoft};

  @media (max-width: ${bp.md}) {
    font-size: 13.5px;
  }
`;

export const PickupStrong = styled.strong`
  color: ${c.ink};
`;

export const PickupMuted = styled.span`
  color: ${c.ink};
  font-weight: 700;
`;

export const PickupLink = styled.a`
  color: ${c.ink};
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid rgba(201, 162, 39, 0.28);
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: ${c.pinkDeep};
    border-color: rgba(232, 138, 178, 0.36);
  }
`;

export const SectionCard = styled.section`
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #fffdf9 100%);
  border: 1px solid ${c.borderStrong};
  border-radius: 20px;
  padding: 16px;
  box-shadow: ${c.shadowSoft};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 20px;
    pointer-events: none;
    box-shadow: inset 0 0 0 1px rgba(201, 162, 39, 0.04);
  }

  @media (max-width: ${bp.md}) {
    padding: 14px;
    border-radius: 16px;

    &::before {
      border-radius: 16px;
    }
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

export const SectionEyebrow = styled.div`
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${c.pinkDeep};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 28px;
  font-family: "Cormorant Garamond", serif;
  font-weight: 650;
  color: ${c.ink};
  line-height: 1.02;

  @media (max-width: ${bp.md}) {
    font-size: 23px;
  }
`;

export const SectionHint = styled.p`
  margin: 5px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${c.inkSoft};
`;

export const DateRow = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 300px) minmax(0, 1fr);
  gap: 12px;
  align-items: center;

  @media (max-width: ${bp.md}) {
    grid-template-columns: 1fr;
  }
`;

export const DateInputWrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  border-radius: 14px;
`;

export const DateInput = styled.input`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  height: 48px;
  border-radius: 14px;
  border: 1px solid rgba(201, 162, 39, 0.18);
  background: #fff;
  padding: 0 52px 0 13px;

  font-family: Georgia, "Times New Roman", Times, serif;
  font-size: 14px;
  color: ${c.ink};
  outline: none;

  appearance: none;
  -webkit-appearance: none;

  &::-webkit-date-and-time-value {
    text-align: left;
  }

  &::-webkit-calendar-picker-indicator {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 21px;
    height: 21px;
    margin: 0;
    padding: 0;
    cursor: pointer;
  }

  &:focus {
    border-color: rgba(232, 138, 178, 0.34);
    box-shadow: 0 0 0 4px ${c.pinkSoft};
  }

  @media (max-width: ${bp.md}) {
    height: 56px;
    font-size: 16px;
    padding-left: 14px;
    padding-right: 54px;

    &::-webkit-calendar-picker-indicator {
      right: 16px;
    }
  }
`;

export const DatePreview = styled.div`
  min-height: 48px;
  display: flex;
  align-items: center;
  padding: 0 13px;
  border-radius: 14px;
  background: linear-gradient(
    180deg,
    rgba(232, 138, 178, 0.04) 0%,
    rgba(201, 162, 39, 0.03) 100%
  );
  border: 1px solid rgba(232, 138, 178, 0.1);
  font-size: 13.5px;
  color: ${c.ink};
  line-height: 1.5;

  @media (max-width: ${bp.md}) {
    min-height: 44px;
    font-size: 13px;
  }
`;

export const SlotHint = styled.p`
  margin: 0 0 10px;
  font-size: 12.8px;
  color: ${c.inkSoft};

  strong {
    color: ${c.ink};
  }
`;

export const SlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(100px, 1fr));
  gap: 8px;

  @media (max-width: ${bp.sm}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const SlotButton = styled.button`
  min-height: 48px;
  padding: 0 10px;
  border-radius: 14px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(232,138,178,0.42)" : "rgba(23,23,23,0.08)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(180deg, rgba(232,138,178,0.92) 0%, rgba(217,94,151,0.92) 100%)"
      : "linear-gradient(180deg, #ffffff 0%, #fffdf9 100%)"};
  color: ${({ $active }) => ($active ? "#ffffff" : c.ink)};
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1;
  cursor: pointer;
  box-shadow: ${({ $active }) =>
    $active ? "0 8px 18px rgba(232,138,178,0.12)" : "none"};
  transition:
    transform 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(232, 138, 178, 0.24);
  }

  @media (max-width: ${bp.md}) {
    min-height: 46px;
    font-size: 16px;
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: ${bp.md}) {
    grid-template-columns: 1fr;
  }
`;

export const FieldGroup = styled.div`
  display: grid;
  gap: 6px;
`;

export const FieldGroupWide = styled(FieldGroup)`
  grid-column: 1 / -1;
`;

export const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 800;
  color: ${c.inkSoft};
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

export const Input = styled.input`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-height: 46px;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fff;
  padding: 0 13px;
  font-size: 14px;
  color: ${c.ink};
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus {
    border-color: rgba(232, 138, 178, 0.34);
    box-shadow: 0 0 0 4px ${c.pinkSoft};
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-height: 86px;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fff;
  padding: 12px 13px;
  font-size: 14px;
  color: ${c.ink};
  resize: vertical;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus {
    border-color: rgba(232, 138, 178, 0.34);
    box-shadow: 0 0 0 4px ${c.pinkSoft};
  }
`;

export const ErrorText = styled.p`
  margin: 6px 0 0;
  font-size: 12.5px;
  color: ${c.error};
  line-height: 1.4;
  font-weight: 700;
`;

export const SuccessText = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${c.success};
  line-height: 1.5;
  font-weight: 700;
`;

export const AlertText = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${c.error};
  font-weight: 700;
`;

export const OrderList = styled.div`
  display: grid;
  gap: 8px;
`;

export const OrderItem = styled.article`
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 11px;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  background: linear-gradient(180deg, #ffffff 0%, #fffdf9 100%);

  @media (max-width: ${bp.md}) {
    grid-template-columns: 54px minmax(0, 1fr) auto;
    gap: 9px;
    padding: 9px 10px;
  }
`;

export const ProductImage = styled.img`
  width: 58px;
  height: 58px;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid rgba(201, 162, 39, 0.12);
  background: #fff;

  @media (max-width: ${bp.md}) {
    width: 54px;
    height: 54px;
  }
`;

export const ProductInfo = styled.div`
  min-width: 0;
  display: grid;
  gap: 3px;
`;

export const ProductName = styled.h3`
  margin: 0;
  font-size: 17px;
  font-family: "Cormorant Garamond", serif;
  font-weight: 650;
  line-height: 1.05;
  color: ${c.ink};

  @media (max-width: ${bp.md}) {
    font-size: 15.5px;
  }
`;

export const ProductMeta = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  color: #7c6954;
`;

export const ProductPrice = styled.div`
  white-space: nowrap;
  font-size: 17px;
  font-weight: 800;
  color: ${c.pinkDeep};

  @media (max-width: ${bp.md}) {
    font-size: 16px;
  }
`;

export const Summary = styled.aside`
  position: sticky;
  top: 132px;
  align-self: start;
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(232, 138, 178, 0.14);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(255, 251, 253, 0.98) 100%
  );
  box-shadow: ${c.shadowCard};

  &::before {
    content: "";
    display: block;
    height: 1px;
    margin-bottom: 2px;
    background: linear-gradient(90deg, transparent, ${c.goldLine}, transparent);
  }

  @media (max-width: ${bp.lg}) {
    position: static;
  }

  @media (max-width: ${bp.md}) {
    padding: 15px;
    border-radius: 18px;
  }
`;

export const SummaryTitle = styled.h2`
  margin: 0;
  font-size: 28px;
  font-family: "Cormorant Garamond", serif;
  font-weight: 650;
  color: ${c.ink};

  @media (max-width: ${bp.md}) {
    font-size: 24px;
  }
`;

export const SummaryText = styled.p`
  margin: -6px 0 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: ${c.inkSoft};
`;

export const SummaryBox = styled.div`
  display: grid;
  gap: 10px;
  padding: 10px;
  border-radius: 18px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  background: linear-gradient(
    180deg,
    rgba(201, 162, 39, 0.03) 0%,
    rgba(232, 138, 178, 0.04) 100%
  );
`;

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 15px;
  color: ${c.inkSoft};

  strong {
    color: ${c.ink};
    font-size: 30px;
    font-weight: 800;
    line-height: 1;
  }

  b {
    color: ${c.ink};
    font-weight: 700;
  }

  @media (max-width: ${bp.md}) {
    strong {
      font-size: 24px;
    }
  }
`;

export const SummaryDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(201, 162, 39, 0.24),
    transparent
  );
`;

export const ButtonRow = styled.div`
  display: grid;
  gap: 10px;
`;

export const PrimaryBtn = styled.button`
  height: 50px;
  border: 0;
  border-radius: 15px;
  background: linear-gradient(180deg, ${c.gold} 0%, ${c.goldDark} 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(183, 140, 26, 0.16);
  transition:
    filter 0.15s ease,
    transform 0.15s ease,
    opacity 0.15s ease;

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const SecondaryBtn = styled.button`
  height: 46px;
  border-radius: 15px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  color: ${c.ink};
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: rgba(232, 138, 178, 0.22);
    background: rgba(232, 138, 178, 0.04);
    color: ${c.pinkDeep};
  }
`;

export const PaymentOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: ${bp.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const PaymentOption = styled.button`
  min-height: 52px;
  padding: 0 14px;
  border-radius: 14px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(217,94,151,0.26)" : "rgba(23,23,23,0.08)"};
  background: ${({ $active }) => ($active ? "rgba(255,248,250,0.9)" : "#fff")};
  color: ${({ $active }) => ($active ? c.ink : c.inkSoft)};
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;

  box-shadow: ${({ $active }) =>
    $active ? "0 4px 12px rgba(217,94,151,0.06)" : "none"};

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(232, 138, 178, 0.24);
  }
`;

export const SelectField = styled.select`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  height: 52px;
  padding: 0 16px;
  border-radius: 14px;
  border: 1px solid rgba(201, 162, 39, 0.35);
  background: #fff;
  color: #171717;
  font-size: 16px;
  font-family: inherit;
  outline: none;

  &:focus {
    border-color: #c9a227;
    box-shadow: 0 0 0 3px rgba(201, 162, 39, 0.12);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const CalendarIcon = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: grid;
  place-items: center;
  pointer-events: none;
  color: #3d3d3d;
  z-index: 2;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const SummaryRowMuted = styled(SummaryRow)`
  strong {
    font-size: 15px;
    font-weight: 700;
    opacity: 0.72;
    color: #7c6954;
  }

  @media (max-width: ${bp.md}) {
    strong {
      font-size: 18px;
    }
  }
`;

export const SummaryRowTotal = styled(SummaryRow)`
  strong {
    font-size: 25px;
    font-weight: 800;
    color: ${c.ink};
    line-height: 1;
  }

  @media (max-width: ${bp.md}) {
    strong {
      font-size: 28px;
    }
  }
`;

export const SummaryRowDiscount = styled(SummaryRow)`
  strong {
    color: #4f7d57;
    font-size: 20px;
    font-weight: 800;
  }

  span {
    color: #4f7d57;
    font-weight: 700;
  }

  @media (max-width: ${bp.md}) {
    strong {
      font-size: 21px;
    }
  }
`;
